import React from 'react';
import Paper from '@material-ui/core/Paper';
import { t } from 'ttag';
import DataTable from '../../../components/DataTable';
import { BASE_STATUS_TITLES, USER_STATUS } from '../../../utils/enums';
import useApi, { MyClubCApi } from '../../../api';
import Grid from '@material-ui/core/Grid';
import Pagination from '@material-ui/lab/Pagination';
import queryString from 'query-string';
import { useApolloClient } from '@apollo/react-hooks';
import useRouter from 'use-react-router';
import { useNavigator } from '../../../components/Navigator';
import { extractDataError } from '../../../utils/errors';
import useNotifier from '../../../components/Messages/hooks';
import { MTableToolbar } from 'material-table';
import Tooltip from '@material-ui/core/Tooltip';
import FilterListIcon from '@material-ui/icons/FilterList';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { SearchInput } from '../../../components/MaterialToolBarSearchInput';
import Loading from '../../../components/Loading';

const ListView = () => {
  const { history } = useRouter();
  const navigate = useNavigator();
  const pushMessage = useNotifier();

  const api = useApi();
  const client = useApolloClient();
  const newApi = new MyClubCApi(client);

  const [groups, setGroups] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const [pageInfo, setPageInfo] = React.useState({});
  const searchParams = new URLSearchParams(history.location.search);

  const [trainers, setTrainers] = React.useState([]);
  const [gyms, setGyms] = React.useState([]);
  const [sections, setSections] = React.useState([]);
  const [pageState, setPageState] = React.useState({
    page: parseInt(searchParams.get('page'), 10) || 1,
    limit: 50,

    trainerId: parseInt(searchParams.get('trainerId'), 10) || undefined,
    gymId: parseInt(searchParams.get('gymId'), 10) || undefined,
    sectionTypeId: parseInt(searchParams.get('sectionTypeId'), 10) || undefined,
    searchQuery: searchParams.get('query') || undefined,
  });

  const updateHistory = (searchParams) => {
    history.replace({
      search: `?${queryString.stringify(searchParams)}`,
    });
  };

  const fetchGroups = async () => {
    setLoading(true);
    const finalParams = Object.fromEntries(
      Object.entries(pageState).filter(([, value]) => !!value)
    );
    updateHistory(finalParams);
    const data = await newApi.getPaginatedGroups(pageState);
    setGroups(data.items);
    setPageInfo(data.pageInfo);
    setLoading(false);
  };

  React.useEffect(() => {
    fetchGroups();
  }, [pageState]);

  React.useEffect(() => {
    async function fetchFilterOptions() {
      const trainers = await newApi.getTrainers({ status: USER_STATUS.active });
      const gyms = await newApi.getGyms();
      const sections = await newApi.getSections();
      setTrainers(trainers);
      setGyms(gyms);
      setSections(sections);
    }
    fetchFilterOptions();
  }, []);

  const onDelete = async (data) => {
    const res = await newApi.deleteGroup(data.id);
    if (res.ok) {
      pushMessage({ text: t`Успішно видалено` });
    } else {
      const dataError = extractDataError(res.errors);
      pushMessage({ text: dataError });
    }
    await fetchGroups();
  };

  const handleSetFilter = async (name, value) => {
    const newPageState = { ...pageState, page: 1, [name]: value };
    setPageState(newPageState);
  };

  const columns = [
    { title: t`Id`, field: 'id', editable: 'never' },
    { title: t`Назва`, field: 'name' },
    {
      title: t`Вид спорту`,
      field: 'section',
      render: ({ sectionObj }) => (sectionObj && sectionObj.name) || '',
    },
    {
      title: t`Зал`,
      field: 'gym_id',
      render: ({ gym }) => (gym && gym.name) || '',
    },
    {
      title: t`Тренер`,
      field: 'trainer',
      render: ({ trainer }) => (trainer && trainer.fullName) || '',
    },
    { title: t`Спортсменів`, field: 'countAthletes' },
    {
      title: t`Статус`,
      field: 'status',
      render: ({ status }) => BASE_STATUS_TITLES[status],
    },
  ];

  if (loading) return <Loading />;

  return (
    <Paper>
      <DataTable
        search={false}
        pageSize={Math.min(pageState.limit, groups.length)}
        isLoading={loading}
        title={t`Список груп`}
        columns={columns}
        data={groups}
        onAdd={() => {
          navigate(api.router.groups.create());
        }}
        onEdit={(e, data) => {
          navigate(api.router.groups.edit(data.id));
        }}
        onDelete={onDelete}
        components={{
          Pagination: () => (
            <Grid container style={{ marginTop: '10px', marginBottom: '10px' }}>
              <Grid item style={{ margin: '0 auto' }}>
                <Pagination
                  size='medium'
                  count={pageInfo.pages}
                  page={pageState.page}
                  color='primary'
                  onChange={(event, page) =>
                    setPageState({ ...pageState, page: page })
                  }
                />
              </Grid>
            </Grid>
          ),
          Toolbar: (props) => (
            <div style={{ flexGrow: 1 }}>
              <MTableToolbar {...props} />
              <Grid
                container
                spacing={2}
                style={{ marginLeft: '10px' }}
                justifyContent='flex-start'
                alignItems='center'
              >
                <Grid item>
                  <Tooltip title={t`Виберіть фільтр`}>
                    <FilterListIcon />
                  </Tooltip>
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={trainers}
                    getOptionLabel={(option) => option.fullInitials}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть тренера`}
                        variant='outlined'
                      />
                    )}
                    value={trainers.find(
                      (item) => item && item.id === pageState.trainerId
                    )}
                    onChange={(e, value) =>
                      handleSetFilter('trainerId', value && value.id)
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={gyms}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть зал`}
                        variant='outlined'
                      />
                    )}
                    value={gyms.find((g) => g && g.id === pageState.gymId)}
                    onChange={(e, value) =>
                      handleSetFilter('gymId', value && value.id)
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={sections}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть вид спорту`}
                        variant='outlined'
                      />
                    )}
                    value={sections.find(
                      (section) =>
                        section && section.id === pageState.sectionTypeId
                    )}
                    onChange={(e, value) =>
                      handleSetFilter('sectionTypeId', value && value.id)
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <SearchInput
                    value={pageState.searchQuery}
                    onChange={(value) => handleSetFilter('searchQuery', value)}
                  />
                </Grid>
              </Grid>
              {!loading && (
                <Grid container style={{ margin: '10px 0 0 15px' }}>
                  <Grid item>
                    <Typography>
                      {t`Знайдено `} <b>{pageInfo.total}</b>{' '}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </div>
          ),
        }}
      />
    </Paper>
  );
};

export default ListView;
